import React, { useState, useRef, useEffect, memo, useContext } from 'react';
import PropTypes from 'prop-types';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var globalProps = /*#__PURE__*/React.createContext();

var hex2rgb = function hex2rgb(hex) {
  var opacity = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 10;
  var c = typeof hex === 'string' ? parseInt(hex.replace('#', ''), 16) : hex;
  return "rgba(".concat(c >> 16, ",\n    ").concat((c & 0xff00) >> 8, ", \n    ").concat(c & 0xff, ", \n    ").concat(opacity / 100, ")");
};

var styleObj = function styleObj(_ref) {
  var stroke = _ref.stroke,
      colorSlice = _ref.colorSlice,
      colorCircle = _ref.colorCircle,
      opacity = _ref.opacity,
      size = _ref.size;
  var boxShadow = colorCircle === undefined ? "inset 0px 0px ".concat(stroke, "px ").concat(stroke, "px ").concat(hex2rgb(colorSlice, opacity)) : '';
  return {
    width: size,
    height: size,
    borderRadius: '50%',
    boxShadow: boxShadow
  };
};

var useOnScreen = function useOnScreen(ref) {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isIntersecting = _useState2[0],
      setIntersecting = _useState2[1];

  useEffect(function () {
    var config = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };
    var observer = new IntersectionObserver(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 1),
          entry = _ref3[0];

      if (entry.intersectionRatio >= 0.5) {
        setIntersecting(entry.isIntersecting);
      }
    }, config);
    if (ref && ref.current) observer.observe(ref.current);
    return function () {
      observer.disconnect();
    };
  }, [ref]);
  return isIntersecting;
};

var GradientLinear = function GradientLinear(_ref4) {
  var index = _ref4.index;

  var _useContext = useContext(globalProps),
      linearGradient = _useContext.linearGradient;

  var number = -100;
  var id = "linear-gradient-".concat(index);
  return /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
    id: id
  }, linearGradient.map(function (gradient, index) {
    number += 100;
    return /*#__PURE__*/React.createElement("stop", {
      key: index,
      offset: number / (linearGradient.length - 1) + '%',
      stopColor: gradient
    });
  })));
};

var CircleTop = /*#__PURE__*/memo(function () {
  var _useContext2 = useContext(globalProps),
      id = _useContext2.id,
      linearGradient = _useContext2.linearGradient,
      counter = _useContext2.counter,
      stroke = _useContext2.stroke,
      round = _useContext2.round,
      colorSlice = _useContext2.colorSlice;

  var gradient = linearGradient !== undefined ? "url(#linear-gradient-".concat(id || 0, ")") : colorSlice;
  return /*#__PURE__*/React.createElement(React.Fragment, null, linearGradient && /*#__PURE__*/React.createElement(GradientLinear, {
    index: id || 0,
    linearGradient: linearGradient
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "50",
    cy: "50",
    r: "42",
    shapeRendering: "geometricPrecision",
    fill: "none",
    transform: "rotate(-90, 50, 50)",
    stroke: gradient,
    strokeWidth: stroke,
    strokeLinecap: round ? 'round' : 'butt',
    strokeDasharray: counter * 2.64 + ', 20000'
  }));
});
var CircleBackground = /*#__PURE__*/memo(function () {
  var _useContext3 = useContext(globalProps),
      stroke = _useContext3.stroke,
      strokeBottom = _useContext3.strokeBottom,
      colorCircle = _useContext3.colorCircle;

  return /*#__PURE__*/React.createElement("circle", {
    cx: "50",
    cy: "50",
    r: "42",
    shapeRendering: "geometricPrecision",
    fill: "none",
    stroke: colorCircle,
    strokeWidth: strokeBottom || stroke
  });
});
var CircleText = /*#__PURE__*/memo(function () {
  var _useContext4 = useContext(globalProps),
      number = _useContext4.number,
      counter = _useContext4.counter,
      fontSize = _useContext4.fontSize,
      fontWeight = _useContext4.fontWeight,
      fontColor = _useContext4.fontColor;

  return number ? /*#__PURE__*/React.createElement("text", {
    x: "50%",
    y: "50%",
    fontSize: fontSize,
    fontWeight: fontWeight,
    fill: fontColor,
    textAnchor: "middle",
    dominantBaseline: "central"
  }, counter, "%") : null;
});
var CircleWrapper = /*#__PURE__*/memo(function () {
  var _useContext5 = useContext(globalProps),
      id = _useContext5.id,
      size = _useContext5.size;

  return /*#__PURE__*/React.createElement("svg", {
    role: "img",
    width: size,
    height: size,
    "data-index": id || 0,
    viewBox: "0 0 100 100",
    "aria-labelledby": "circular progress bar"
  }, /*#__PURE__*/React.createElement(CircleText, null), /*#__PURE__*/React.createElement(CircleBackground, null), /*#__PURE__*/React.createElement(CircleTop, null));
});

var CircularProgressBar = function CircularProgressBar(props) {
  var percent = props.percent,
      speed = props.speed;

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      counter = _useState4[0],
      setCounter = _useState4[1];

  var ref = useRef();
  var show = useOnScreen(ref);
  useEffect(function () {
    var _ref$current, _ref$current$dataset;

    if (!show) return;
    var angle = Number((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : (_ref$current$dataset = _ref$current.dataset) === null || _ref$current$dataset === void 0 ? void 0 : _ref$current$dataset.angel);
    if (percent > 100 || percent < 0 || angle === percent) return;
    var fps = speed || 60;
    var interval = 1000 / fps;
    var tolerance = 0.1;
    var then = performance.now();
    var request;

    var animateLoop = function animateLoop(now) {
      request = requestAnimationFrame(animateLoop);
      var delta = now - then;

      if (delta >= interval - tolerance) {
        then = now - delta % interval;

        if (angle > percent) {
          setCounter(function (count) {
            return count - 1;
          });
        } else if (angle < percent) {
          setCounter(function (count) {
            return count + 1;
          });
        }
      }
    };

    request = requestAnimationFrame(animateLoop);
    return function () {
      return cancelAnimationFrame(request);
    };
  }, [counter, speed, show, percent]);

  var value = _objectSpread2(_objectSpread2({}, props), {}, {
    counter: counter
  });

  return /*#__PURE__*/React.createElement(globalProps.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: styleObj(props),
    "data-angel": counter
  }, /*#__PURE__*/React.createElement(CircleWrapper, null)));
};

CircularProgressBar.propTypes = {
  percent: PropTypes.number.isRequired,
  colorSlice: PropTypes.string,
  colorCircle: PropTypes.string,
  stroke: PropTypes.number,
  strokeBottom: PropTypes.number,
  round: PropTypes.bool,
  opacity: PropTypes.number,
  number: PropTypes.bool,
  size: PropTypes.number,
  speed: PropTypes.number,
  fontSize: PropTypes.string,
  fontWeight: PropTypes.number,
  fontColor: PropTypes.string,
  linearGradient: PropTypes.array
};
CircularProgressBar.defaultProps = {
  colorSlice: '#00a1ff',
  round: false,
  number: true,
  stroke: 10,
  opacity: 10,
  size: 200,
  fontSize: '1.6rem',
  fontColor: '#365b74',
  fontWeight: 400
};

export { CircularProgressBar };
